import { FC } from 'react';
import { Link } from 'react-router-dom';
import { User } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { statusStyles, type TherapistAppointment } from './types';
import { formatTime } from './dateUtils';

interface AppointmentCardProps {
  appointment: TherapistAppointment;
  compact?: boolean;
}

export const AppointmentCard: FC<AppointmentCardProps> = ({ appointment, compact = false }) => {
  const style = statusStyles[appointment.status];
  const startTime = formatTime(appointment.startTime);
  const endTime = formatTime(appointment.endTime);

  if (compact) {
    return (
      <Link
        to={`/therapist/appointments/${appointment.id}`}
        className={cn(
          'block p-2 rounded-md text-xs border-l-4 transition-shadow hover:shadow-md',
          style.bg,
          style.darkBg,
          style.border
        )}
      >
        <div className={cn('font-medium truncate', style.text, style.darkText)}>{startTime}</div>
        <div className="text-gray-700 dark:text-slate-300 truncate">{appointment.clientName}</div>
        <div className="text-gray-500 dark:text-slate-400 truncate">{appointment.serviceName}</div>
      </Link>
    );
  }

  return (
    <div
      className={cn(
        'bg-white dark:bg-slate-800 rounded-lg border p-4 transition-shadow hover:shadow-md',
        style.border
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {appointment.clientAvatar ? (
            <img
              src={appointment.clientAvatar}
              alt={appointment.clientName}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-gray-500 dark:text-slate-400" />
            </div>
          )}
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900 dark:text-slate-100 truncate">
              {appointment.clientName}
            </h4>
            <p className="text-sm text-gray-600 dark:text-slate-400 truncate">
              {appointment.serviceName} - {appointment.duration} min
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">
            {startTime} - {endTime}
          </p>
          <span
            className={cn(
              'inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full',
              style.bg,
              style.text,
              style.darkBg,
              style.darkText
            )}
          >
            {style.label}
          </span>
        </div>
      </div>
      {appointment.specialRequests && (
        <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800">
          <strong>Note:</strong> {appointment.specialRequests}
        </div>
      )}
      <div className="mt-3 flex justify-end">
        <Link
          to={`/therapist/appointments/${appointment.id}`}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
        >
          View Details
        </Link>
      </div>
    </div>
  );
};
